import { CloseOutlined } from '@/app/icons';
import { App, Button, Card, Flex, Modal, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { usePhoneLayout } from '@/hooks/use-media-query';
import { usePwaStore } from '@/stores/pwa-store';
import { BrandMark } from './BrandMark';

const DISMISSED_KEY = 'halo-install-dismissed';

function readDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * PWA (03/10/2026, chủ dự án: «triển khai giao diện PWA trên điện thoại») — ba thứ đi theo khung ứng dụng:
 *
 *  1. LỜI MỜI CÀI trên điện thoại đang mở bằng trình duyệt: một hàng gọn ở đầu trang, bấm «Cài đặt» là
 *     xong; đóng đi thì máy ấy không bị mời lại (vẫn cài được từ menu tài khoản).
 *  2. HƯỚNG DẪN cho iPhone: Apple không cho trang web tự mở hộp cài, phải đi qua nút Chia sẻ của Safari.
 *  3. THÔNG BÁO BẢN MỚI: ứng dụng trên điện thoại mở suốt nhiều ngày và không có nút tải lại — khi bản
 *     mới đã về máy thì mời tải lại, không tự tải (đang gõ dở sẽ mất).
 */
export function InstallApp() {
  const { notification } = App.useApp();
  const isPhone = usePhoneLayout();
  const mode = usePwaStore((state) => state.mode);
  const updateReady = usePwaStore((state) => state.updateReady);
  const iosHelpOpen = usePwaStore((state) => state.iosHelpOpen);
  const requestInstall = usePwaStore((state) => state.requestInstall);
  const closeIosHelp = usePwaStore((state) => state.closeIosHelp);
  const [dismissed, setDismissed] = useState(readDismissed);

  useEffect(() => {
    if (!updateReady) return;
    notification.info({
      key: 'pwa-update',
      title: 'Đã có bản mới',
      description: 'Tải lại để dùng bản vừa cập nhật.',
      duration: 0,
      placement: 'bottomRight',
      actions: (
        <Button type="primary" size="small" onClick={() => window.location.reload()}>
          Tải lại
        </Button>
      ),
    });
  }, [updateReady, notification]);

  const dismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISSED_KEY, '1');
    } catch {
      /* không nhớ được thì lần sau mời lại — không sao */
    }
  };

  const invite = isPhone && !dismissed && (mode === 'prompt' || mode === 'ios');

  return (
    <>
      {invite && (
        <Card size="small" style={{ marginBottom: 12 }} styles={{ body: { padding: '8px 12px' } }}>
          <Flex align="center" gap={10}>
            <BrandMark size={32} />
            <Typography.Text style={{ flex: 1, minWidth: 0 }}>
              Cài SUN MUSIC lên màn hình chính
            </Typography.Text>
            <Button type="primary" onClick={() => void requestInstall()}>
              Cài đặt
            </Button>
            <Button
              type="text"
              icon={<CloseOutlined />}
              aria-label="Ẩn lời mời cài ứng dụng"
              onClick={dismiss}
            />
          </Flex>
        </Card>
      )}
      <Modal
        open={iosHelpOpen}
        title="Cài SUN MUSIC trên iPhone"
        footer={null}
        onCancel={closeIosHelp}
      >
        <ol style={{ margin: 0, paddingInlineStart: 20, lineHeight: 2 }}>
          <li>
            Mở trang này bằng <b>Safari</b>.
          </li>
          <li>
            Bấm nút <b>Chia sẻ</b> (ô vuông có mũi tên hướng lên).
          </li>
          <li>
            Chọn <b>«Thêm vào Màn hình chính»</b>, rồi bấm <b>Thêm</b>.
          </li>
        </ol>
      </Modal>
    </>
  );
}
